import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePartnerSettings, DocumentType, DocumentRequirements } from '@/hooks/usePartnerSettings';
import { Plus, Pencil, Trash2, Settings2 } from 'lucide-react';
import { RequirementsConfigurator } from './RequirementsConfigurator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const CATEGORIES = ['Insurance', 'License', 'Financial', 'Certification', 'Safety', 'Legal', 'Other'];
const ROLES = ['tenant_admin', 'legal', 'project_manager', 'estimator'];

export function DocumentTypesManager() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<DocumentType | null>(null);
  const [formData, setFormData] = useState<Partial<DocumentType>>({
    id: '',
    name: '',
    category: 'Insurance',
    required_for_prequalification: false,
    has_expiry: false,
    verification_required: false,
    verification_roles: [],
    reminder_days_before_expiry: null,
    requirements: {},
  });
  const [showRequirements, setShowRequirements] = useState(false);

  useEffect(() => {
    if (settings?.document_types) {
      setDocumentTypes(settings.document_types);
    }
  }, [settings]);

  const handleSave = async () => {
    const newDoc: DocumentType = {
      id: editingDoc?.id || formData.id!.toLowerCase().replace(/\s+/g, '_'),
      name: formData.name!,
      category: formData.category!,
      required_for_prequalification: formData.required_for_prequalification!,
      has_expiry: formData.has_expiry!,
      verification_required: formData.verification_required!,
      verification_roles: formData.verification_roles!,
      reminder_days_before_expiry: formData.has_expiry ? formData.reminder_days_before_expiry : null,
      requirements: formData.requirements || {},
    };

    const updatedDocs = editingDoc
      ? documentTypes.map(d => d.id === editingDoc.id ? newDoc : d)
      : [...documentTypes, newDoc];

    await updateSettings({ document_types: updatedDocs });
    setDocumentTypes(updatedDocs);
    setDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document type?')) return;
    const updatedDocs = documentTypes.filter(d => d.id !== docId);
    await updateSettings({ document_types: updatedDocs });
    setDocumentTypes(updatedDocs);
  };

  const openEditDialog = (doc: DocumentType) => {
    setEditingDoc(doc);
    setFormData({
      ...doc,
      requirements: doc.requirements || {},
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingDoc(null);
    setShowRequirements(false);
    setFormData({
      id: '',
      name: '',
      category: 'Insurance',
      required_for_prequalification: false,
      has_expiry: false,
      verification_required: false,
      verification_roles: [],
      reminder_days_before_expiry: null,
      requirements: {},
    });
  };

  const toggleRole = (role: string) => {
    const roles = formData.verification_roles || [];
    setFormData({
      ...formData,
      verification_roles: roles.includes(role)
        ? roles.filter(r => r !== role)
        : [...roles, role],
    });
  };

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  const groupedDocs = documentTypes.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, DocumentType[]>);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Document Types</CardTitle>
            <CardDescription>
              Configure required document types and verification settings
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Add Document Type
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingDoc ? 'Edit Document Type' : 'Add New Document Type'}
                </DialogTitle>
                <DialogDescription>
                  Configure document requirements and verification settings
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {!editingDoc && (
                  <div className="space-y-2">
                    <Label htmlFor="id">ID</Label>
                    <Input
                      id="id"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      placeholder="general_liability"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="General Liability Insurance"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Required for Prequalification</Label>
                  <Switch
                    checked={formData.required_for_prequalification}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, required_for_prequalification: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Has Expiry Date</Label>
                  <Switch
                    checked={formData.has_expiry}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, has_expiry: checked })
                    }
                  />
                </div>
                {formData.has_expiry && (
                  <div className="space-y-2">
                    <Label htmlFor="reminder">Reminder Days Before Expiry</Label>
                    <Input
                      id="reminder"
                      type="number"
                      value={formData.reminder_days_before_expiry || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          reminder_days_before_expiry: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      placeholder="30"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Label>Verification Required</Label>
                  <Switch
                    checked={formData.verification_required}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, verification_required: checked })
                    }
                  />
                </div>
                {formData.verification_required && (
                  <div className="space-y-2">
                    <Label>Verification Roles</Label>
                    <div className="space-y-2">
                      {ROLES.map(role => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={role}
                            checked={formData.verification_roles?.includes(role)}
                            onCheckedChange={() => toggleRole(role)}
                          />
                          <Label htmlFor={role} className="font-normal cursor-pointer">
                            {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Label>
                        </div>
                      ))}
                     </div>
                   </div>
                 )}
                 
                 {/* Requirements Configuration */}
                 <div className="pt-4 border-t">
                   <Button
                     type="button"
                     variant="outline"
                     className="w-full"
                     onClick={() => setShowRequirements(!showRequirements)}
                   >
                     <Settings2 className="h-4 w-4 mr-2" />
                     {showRequirements ? 'Hide' : 'Configure'} Validation Requirements
                   </Button>
                   
                   {showRequirements && (
                     <div className="mt-4">
                       <RequirementsConfigurator
                         category={formData.category!}
                         requirements={formData.requirements || {}}
                         onChange={(requirements) => setFormData({ ...formData, requirements })}
                       />
                     </div>
                   )}
                 </div>
               </div>
               <DialogFooter>
                 <Button variant="outline" onClick={() => setDialogOpen(false)}>
                   Cancel
                 </Button>
                 <Button
                   onClick={handleSave}
                   disabled={!formData.name || (!editingDoc && !formData.id)}
                 >
                   {editingDoc ? 'Update' : 'Create'}
                 </Button>
               </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.keys(groupedDocs).length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No document types configured. Add one to get started.
          </div>
        ) : (
          Object.entries(groupedDocs).map(([category, docs]) => (
            <div key={category} className="space-y-3">
              <h3 className="font-medium text-lg">{category}</h3>
              <div className="grid gap-3">
                {docs.map((doc) => (
                  <Card key={doc.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{doc.name}</h4>
                            <Badge variant="outline">{doc.category}</Badge>
                            {doc.required_for_prequalification && (
                              <Badge variant="destructive">Required</Badge>
                            )}
                          </div>
                           <div className="text-sm text-muted-foreground space-y-1">
                             <div>Has Expiry: {doc.has_expiry ? 'Yes' : 'No'}</div>
                             {doc.has_expiry && doc.reminder_days_before_expiry && (
                               <div>Reminder: {doc.reminder_days_before_expiry} days before</div>
                             )}
                             <div>Verification: {doc.verification_required ? 'Required' : 'Not Required'}</div>
                             {doc.verification_required && doc.verification_roles.length > 0 && (
                               <div>Roles: {doc.verification_roles.join(', ')}</div>
                             )}
                             {doc.requirements && Object.keys(doc.requirements).length > 0 && (
                               <div className="flex items-center gap-1 text-primary">
                                 <Settings2 className="h-3 w-3" />
                                 {Object.keys(doc.requirements).length} validation rules configured
                               </div>
                             )}
                           </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(doc)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
